
import { useState } from "react"
import type { Seat, Personnel } from "../hooks/types"

interface SeatAssignmentDrawerProps {
  isOpen: boolean
  onClose: () => void
  onAssign: (seatId: string, personnelId: string) => void
  availableSeats: Seat[]
  availablePersonnel: Personnel[]
  isLoading: boolean
  role: "Project Manager" | "Foreman"
}

export default function SeatAssignmentDrawer({
  isOpen,
  onClose,
  onAssign,
  availableSeats,
  availablePersonnel,
  isLoading,
  role,
}: SeatAssignmentDrawerProps) {
  const [selectedSeatId, setSelectedSeatId] = useState<string>("")
  const [selectedPersonnelId, setSelectedPersonnelId] = useState<string>("")

  const handleAssign = () => {
    if (selectedSeatId && selectedPersonnelId) {
      onAssign(selectedSeatId, selectedPersonnelId)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white text-gray-900 rounded-t-lg sm:rounded-lg w-full sm:w-96 max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Assign {role}</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              &times;
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-sm font-medium">Available Seats</label>
              <select
                value={selectedSeatId}
                onChange={(e) => setSelectedSeatId(e.target.value)}
                className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                disabled={isLoading || availableSeats.length === 0}
              >
                <option value="">Select a seat</option>
                {availableSeats.map((seat) => (
                  <option key={seat._id} value={seat._id}>
                    {seat.role} - {seat.userId?.name || "Unassigned"}
                  </option>
                ))}
              </select>
              {availableSeats.length === 0 && <p className="text-sm text-red-500 mt-1">No available seats</p>}
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium">Available Personnel</label>
              <select
                value={selectedPersonnelId}
                onChange={(e) => setSelectedPersonnelId(e.target.value)}
                className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                disabled={isLoading || availablePersonnel.length === 0}
              >
                <option value="">Select personnel</option>
                {availablePersonnel.map((personnel) => (
                  <option key={personnel._id} value={personnel._id}>
                    {personnel.contactId.name}
                  </option>
                ))}
              </select>
              {availablePersonnel.length === 0 && <p className="text-sm text-red-500 mt-1">No available personnel</p>}
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300" disabled={isLoading}>
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={isLoading || !selectedSeatId || !selectedPersonnelId}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Assigning...
                </span>
              ) : (
                "Assign"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
